import React, { useState } from 'react';
import { Form, reduxForm } from 'redux-form';
import { makeStyles } from '@mui/styles';
import { Button, Grid } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import SmscConfigFields from './SmscConfigFields';
import SmscConnectionsFields from './SmscConnectionsFields';
import { SmscObject } from '../../../../models/smscModel';
import TabPanel from '../../../../shared/components/TabPanel';
import { SMSC_FORM_NAME } from '../../../../store/form/formStateModel';
import { VersionableId } from '../../../../types/versionableId';
import SmscConfigFilePreviewContainer from '../../containers/smsc/SmscConfigFilePreviewContainer';
import { smscFormValidationSchema } from '../../validation/smscFormValidation';
import { zodReduxFormValidatorResolver } from '../../../../shared/forms/validation/resolvers/zodReduxFormValidatorResolver';

const useStyles = makeStyles(({
  root: {
    paddingBottom: 20,
    width: '100%',
    height: 'auto',
    flexGrow: 1,
  },
  button: {
    marginRight: '15px'
  },
  buttons: {
    paddingBottom: '10px',
    paddingTop: '10px',
    backgroundColor: '#F5F5F5',
    width: '100%',
    position: 'fixed',
    bottom: 0,
  },
}));

export type SmscDataForm = SmscObject;

type Props = {
  readonly title: string;
  readonly smscId?: VersionableId;
  readonly initialValues?: Partial<SmscDataForm>;
  readonly onSubmit?: (smsc: SmscObject) => void;
  readonly handleSubmit?: (formData: any) => void;
  readonly onCancel: () => void;
  readonly readOnly: boolean;
} & {
  // redux-form props
  readonly handleSubmit?: (data: any) => void;
  readonly invalid?: boolean;
  readonly pristine?: boolean;
};

const SmscForm = (props: Props) => {
  const {
    title,
    smscId,
    onCancel,
    readOnly,
  } = props;

  const { handleSubmit, invalid, pristine } = props;

  const classes = useStyles();

  const [value, setValue] = useState<number>(0);
  const [tab, setTab] = useState<number>(0);

  const handleChangeViewTab = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Grid container>
        <Grid item style={{ width: '100%' }}>
          <AppBar position='static'>
            <Typography variant='h6' style={{ padding: '10px 20px' }}>ESME</Typography>
          </AppBar>
        </Grid>
      </Grid>
      <Grid container>
        <Paper style={{ backgroundColor: '#F5F5F5', width: '100%' }}>
          <Tabs
            style={{ minHeight: '100%' }}
            value={value}
            onChange={handleChangeViewTab}
            indicatorColor='primary'
            textColor='primary'
            variant='scrollable'
          >
            <Tab label={title} />
            <Tab label='Preview' />
          </Tabs>
        </Paper>
        <div className={classes.root}>
          <Grid item xs={12} style={{ paddingRight: 25, paddingLeft: 25 }}>
            <Grid container spacing={3} style={{ display: 'flex', paddingTop: 40 }}>
              <Grid container style={{ paddingRight: 25, paddingLeft: 25 }}>
                <Grid item style={{ width: '100%' }}>
                  <div>
                    <TabPanel value={value} index={0}>
                      <Paper style={{ backgroundColor: '#F5F5F5', width: '100%' }}>
                        <Tabs
                          style={{ minHeight: '100%' }}
                          value={tab}
                          onChange={handleChangeTab}
                          indicatorColor='primary'
                          textColor='primary'
                          variant='scrollable'
                        >
                          <Tab label='Config' />
                          <Tab label='Connections' />
                        </Tabs>
                      </Paper>
                      <Grid item style={{ width: '100%' }}>
                        <div>
                          <TabPanel value={tab} index={0}>
                            <SmscConfigFields readOnly={readOnly} />
                          </TabPanel>
                          <TabPanel value={tab} index={1}>
                            <SmscConnectionsFields readOnly={readOnly} />
                          </TabPanel>
                        </div>
                      </Grid>
                    </TabPanel>
                    <TabPanel value={value} index={1}>
                      <SmscConfigFilePreviewContainer readOnly={readOnly} smscId={smscId} />
                    </TabPanel>
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </Grid>
      <Container className={classes.buttons}>
          { !readOnly && (
            <Button
              type='submit'
              color='primary'
              className={classes.button}
              disabled={invalid || pristine}
            >
              Save
            </Button>
          )}
          <Button onClick={onCancel}>{ readOnly ? 'Close' : 'Cancel' }</Button>
      </Container>
    </Form>
  );
};

export default reduxForm<SmscDataForm, Props>({
  form: SMSC_FORM_NAME,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: false,
  validate: (values) => (
    zodReduxFormValidatorResolver(smscFormValidationSchema)(values)
  ),
})(SmscForm);
